import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  IconButton, 
  Button,
  Avatar,
  Fade,
  Zoom
} from '@mui/material';
import { MessageCircle, Send, X, Minimize2, Maximize2, Bot } from 'lucide-react';

// Define message type
interface Message {
  id: number;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

// Initial bot messages
const initialMessages: Message[] = [
  {
    id: 1,
    text: "👋 Hi there! I'm RiC.  Recruitment Intelligence's AI Consultant. How can I help you with your recruiting needs today?",
    sender: 'bot',
    timestamp: new Date()
  }
];

const keywordResponses: { keywords: string[]; response: string }[] = [
  {
    keywords: ['hours', 'open', 'time'],
    response: "If you need to talk to somebody, check out our Contact page.  Humans are available Monday to Friday, 9am to 5pm.  As for me, well, I'm always here to help!"
  },
  {
    keywords: ['how much','cost', 'pricing', 'price', 'plans'],
    response: "We offer different pricing tiers based on your company size and needs. Checkout our pricing page for more details."
  },
  {
    keywords: ['Ric', 'RIC', 'ric', 'who'],
    response: "That is me, your friendly AI Agent!"
  },
  {
    keywords: ['trial', 'free', 'start'],
    response: "Sign up for our services and you will get guaranteed service via AI and/or with our professional recruiters."
  },
  {
    keywords: ['how','process', 'workflow', 'control'],
    response: "Our AI takes your job posting and does the hard work of finding the best candidates.  You can then review the top candidates and make the final decision."
  },
  {
    keywords: ['what', 'do'],
    response: "I can help you find top-notch candidates, streamline your hiring process, and answer any questions you have about our services."
  },
  {
    keywords: ['demo', 'schedule', 'presentation'],
    response: "I'd be happy to schedule a demo! Click on the 'Schedule a Demo' button on the home page and fill out your information in the contact form and we'll reach out to you shortly to set up a time."
  },
  {
    keywords: ['ats', 'integrate', 'integration'],
    response: "Recruitment Intelligence integrates with multiple platforms.  More are being added everyday.  Request a demo by providing your email and we'll reach out shortly to schedule a demo and discuss integration options."
  },
  {
    keywords: ['bias', 'fair', 'diversity'],
    response: "Our platform uses AI to reduce bias and promote fair hiring."
  },

  {
    keywords: ['where','resumes','source', 'data', 'job boards'],
    response: "Our AI aggregates data from multiple publicly available data sources such as LinkedIn, Stack Overflow, GitHub and other professional and career websites."
  },
  {
    keywords: ['resume', 'match', 'candidate'],
    response: "Our AI analyzes resumes to help match top candidates to your job listings."
  }

];

const getKeywordMatchedResponse = (input: string): string => {
  const lowerInput = input.toLowerCase();
  for (const { keywords, response } of keywordResponses) {
    if (keywords.some((kw) => lowerInput.includes(kw))) {
      return response;
    }
  }
  return "I'm sorry, I didn't catch that. Can you rephrase or ask something else about our recruiting services?";
};

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Auto-scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);
  
  // Toggle chat open/closed
  const toggleChat = () => {
    setIsOpen(!isOpen);
    setIsMinimized(false);
  };
  
  // Toggle minimized state
  const toggleMinimize = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMinimized(!isMinimized);
  };
  
  // Handle sending a new message
  const handleSendMessage = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (newMessage.trim() === '') return;
    
    // Add user message
    const userMessage: Message = {
      id: messages.length + 1,
      text: newMessage,
      sender: 'user',
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    
    // Simulate bot typing
    setIsTyping(true);
    
    // Simulate bot response after a delay
    setTimeout(() => {
      const matchedResponse = getKeywordMatchedResponse(newMessage);
      
      const botMessage: Message = {
        id: messages.length + 2,
        text: matchedResponse,
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
      setIsTyping(false);
    }, 1500);
  };
  
  // Format timestamp
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  return (
    <>
      {/* Floating chat button */}
      <Zoom in={!isOpen}>
        <IconButton
          onClick={toggleChat}
          sx={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            backgroundColor: 'primary.main',
            color: 'white',
            width: 60,
            height: 60,
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
            zIndex: 1000
          }}
        >
          <MessageCircle size={28} />
        </IconButton>
      </Zoom>
      
      {/* Chat window */}
      <Fade in={isOpen}>
        <Paper
          elevation={6}
          sx={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            width: 360,
            height: isMinimized ? 'auto' : 500,
            borderRadius: 2,
            overflow: 'hidden',
            zIndex: 1000,
            display: isOpen ? 'flex' : 'none',
            flexDirection: 'column'
          }}
        >
          {/* Chat header */}
          <Box
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'move'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1,
                  p: 0.5
                }}
              >
                <img
                  src="/assets/RIC_NoText.png"
                  alt="RiC Logo"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
              </Box>
              <Typography variant="subtitle1" fontWeight={600}>
                RiC - Your AI Assistant
              </Typography>
            </Box>
            <Box>
              <IconButton 
                size="small" 
                onClick={toggleMinimize} 
                sx={{ color: 'white', mr: 1 }}
              >
                {isMinimized ? <Maximize2 size={18} /> : <Minimize2 size={18} />}
              </IconButton>
              <IconButton 
                size="small" 
                onClick={toggleChat}
                sx={{ color: 'white' }}
              >
                <X size={18} />
              </IconButton>
            </Box>
          </Box>
          
          {/* Chat messages */}
          {!isMinimized && (
            <Box
              sx={{
                p: 2,
                flexGrow: 1,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                backgroundColor: '#f5f7fa'
              }}
            >
              {messages.map((message) => (
                <Box
                  key={message.id}
                  sx={{
                    display: 'flex',
                    flexDirection: message.sender === 'user' ? 'row-reverse' : 'row',
                    alignItems: 'flex-start',
                    gap: 1
                  }}
                >
                  {message.sender === 'user' ? (
                    <Avatar
                      sx={{
                        bgcolor: 'secondary.main',
                        width: 32,
                        height: 32
                      }}
                    >
                      U
                    </Avatar>
                  ) : (
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 0.25,
                        border: '2px solid',
                        borderColor: 'primary.main'
                      }}
                    >
                      <img
                        src="/assets/RIC_NoText.png"
                        alt="RiC"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain'
                        }}
                      />
                    </Box>
                  )}
                  <Box
                    sx={{
                      maxWidth: '70%',
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: message.sender === 'user' ? 'secondary.light' : 'white',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                      position: 'relative'
                    }}
                  >
                    <Typography variant="body2">{message.text}</Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        textAlign: message.sender === 'user' ? 'left' : 'right',
                        mt: 0.5,
                        color: 'text.secondary'
                      }}
                    >
                      {formatTime(message.timestamp)}
                    </Typography>
                  </Box>
                </Box>
              ))}
              
              {/* Bot typing indicator */}
              {isTyping && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 0.25,
                      border: '2px solid',
                      borderColor: 'primary.main'
                    }}
                  >
                    <img
                      src="/assets/RIC_NoText.png"
                      alt="RiC"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: 'white',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                    }}
                  >
                    <Typography variant="body2">
                      <span className="typing-indicator">
                        <span className="dot"></span>
                        <span className="dot"></span>
                        <span className="dot"></span>
                      </span>
                    </Typography>
                  </Box>
                </Box>
              )}
              
              <div ref={messagesEndRef} />
            </Box>
          )}
          
          {/* Message input */}
          {!isMinimized && (
            <Box
              component="form"
              onSubmit={handleSendMessage}
              sx={{
                p: 2,
                borderTop: '1px solid rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                backgroundColor: 'white'
              }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Type your message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                variant="outlined"
                autoComplete="off"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3
                  }
                }}
              />
              <IconButton
                color="primary"
                onClick={() => handleSendMessage()}
                disabled={newMessage.trim() === ''}
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark'
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'action.disabledBackground',
                    color: 'action.disabled'
                  }
                }}
              >
                <Send size={18} />
              </IconButton>
            </Box>
          )}
          
          {/* Quick actions when minimized */}
          {isMinimized && (
            <Box sx={{ p: 1.5, display: 'flex', gap: 1 }}>
              <Button 
                size="small" 
                variant="outlined" 
                onClick={() => {
                  setIsMinimized(false);
                  setNewMessage("Tell me about RiC");
                  setTimeout(() => handleSendMessage(), 100);
                }}
              >
                About RiC
              </Button>
              <Button 
                size="small" 
                variant="outlined"
                onClick={() => {
                  setIsMinimized(false);
                  setNewMessage("I'd like a demo");
                  setTimeout(() => handleSendMessage(), 100);
                }}
              >
                Request Demo
              </Button>
            </Box>
          )}
        </Paper>
      </Fade>
      
      {/* CSS for typing indicator */}
      <style jsx global>{`
        .typing-indicator {
          display: flex;
          align-items: center;
        }
        
        .dot {
          display: inline-block;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #3f51b5;
          margin-right: 4px;
          animation: typing 3.0s infinite ease-in-out;
        }
        
        .dot:nth-child(1) {
          animation-delay: 0s;
        }
        
        .dot:nth-child(2) {
          animation-delay: 0.2s;
        }
        
        .dot:nth-child(3) {
          animation-delay: 0.4s;
          margin-right: 0;
        }
        
        @keyframes typing {
          0%, 60%, 100% {
            transform: translateY(0);
            opacity: 0.6;
          }
          30% {
            transform: translateY(-4px);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
};

export default Chatbot;